import React, { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ImageIcon, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaPicker({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const openPicker = () => { setOpen(true); load(); };
  const fullUrl = (m) => `${BACKEND_URL}${m.url}`;

  const pick = (m) => { onSelect(fullUrl(m)); setOpen(false); toast.success("Image selected"); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onSelect(`${BACKEND_URL}${data.url}`);
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      <Button type="button" variant="ghost" onClick={openPicker} className="mt-2 h-9 border border-white/10 px-3 text-xs" data-testid="media-picker-open">
        <ImageIcon className="h-3.5 w-3.5" /> Library
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-ink-900 text-white sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between font-heading">
              <span>Media Library</span>
              <label className="mr-6 inline-flex cursor-pointer items-center gap-2 rounded-md bg-vantage px-3 py-1.5 text-xs font-semibold hover:bg-vantage-hover">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-picker-upload" />
              </label>
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-vantage" /></div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-500">No images yet. Upload one above.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {items.map((m) => (
                <button key={m.id} type="button" onClick={() => pick(m)} className="group overflow-hidden rounded-xl border border-white/10 hover:border-vantage" data-testid={`media-pick-${m.id}`}>
                  <img src={fullUrl(m)} alt={m.filename} loading="lazy" className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
